import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CrmAudienceHelperRequest,
  CrmAudienceHelperResponse,
  CrmAudienceList,
  CrmAudienceListDetail,
  CrmAudienceListUploadResult,
  CrmAudienceListUpsert,
  CrmCustomerDetail,
  CrmCustomerEdit,
  CrmCustomerEmailRow,
  CrmCustomerOrderRow,
  CrmCustomerReservationRow,
  CrmCustomerRow,
  CrmCustomersQuery,
  CrmImpersonateResponse,
  CrmImpersonationAuditRow,
  CrmImportResult,
  CrmInterestLabels,
  CrmMovie,
  CrmNewsletter,
  CrmNewsletterPosOfferings,
  CrmNewsletterUpsert,
  CrmProductTag,
  CrmProductTagUpsert,
  CrmSettings,
  DataTablesList,
} from '@digilize/shared/definitions/src';

/**
 * Customer CRM API (handover PAUL-2814). Every route is Moderator-and-up and scoped to a single
 * CompanyGroup. Base path `/api/v1.0/crm`.
 *
 * The methods here stay group-agnostic on purpose: the dashboard's `ActiveCompanyInterceptor` stamps
 * the company the operator is working in on every call as `X-Company-Id` and the backend resolves the
 * group from it (PAUL-3495). An explicit `company_group_id` outranks that header, so only send one
 * from a Pauly-admin context that really means a different group.
 */
@Injectable({ providedIn: 'root' })
export class CrmApiService {
  readonly CRM_URL = '/api/v1.0/crm';

  constructor(private http: HttpClient) {}

  // --- Settings (§3) --------------------------------------------------------
  getSettings() {
    return this.http.get<CrmSettings>(`${this.CRM_URL}/settings`);
  }

  /** Full overwrite — send every field. */
  updateSettings(data: CrmSettings) {
    return this.http.put<CrmSettings>(`${this.CRM_URL}/settings`, data);
  }

  // --- Product tags (§4) ----------------------------------------------------
  /** Bare array, alphabetical. */
  getProductTags() {
    return this.http.get<CrmProductTag[]>(`${this.CRM_URL}/product-tags`);
  }

  createProductTag(data: CrmProductTagUpsert) {
    return this.http.post<CrmProductTag>(`${this.CRM_URL}/product-tags`, data);
  }

  /** ⚠ Response `customer_count` is stubbed 0 — refetch the list after a rename. */
  updateProductTag(id: string, data: CrmProductTagUpsert) {
    return this.http.put<CrmProductTag>(`${this.CRM_URL}/product-tags/${id}`, data);
  }

  /** Soft-deletes the tag with its product attachments and customer assignments. */
  deleteProductTag(id: string) {
    return this.http.delete(`${this.CRM_URL}/product-tags/${id}`);
  }

  /** Fire-and-forget background backfill (no completion signal) — show a toast, not a spinner. */
  applyProductTag(id: string) {
    return this.http.post(`${this.CRM_URL}/product-tags/${id}/apply`, {});
  }

  // --- Value sources for the audience-definition builder (§5/§9) -------------
  getInterestLabels() {
    return this.http.get<CrmInterestLabels>(`${this.CRM_URL}/interest-labels`);
  }

  /** Movie autocomplete — bare array, max 50; empty search = first 50. */
  getMovies(search?: string) {
    return this.http.get<CrmMovie[]>(`${this.CRM_URL}/movies`, {
      params: search ? { search } : {},
    });
  }

  // --- Customers (§5/§6) -----------------------------------------------------
  /** Build `params` with only the set keys (omit undefined). */
  getCustomers(params: CrmCustomersQuery) {
    return this.http.get<DataTablesList<CrmCustomerRow>>(`${this.CRM_URL}/customers`, { params: { ...params } });
  }

  /** Export the current filtered result (paging ignored). Returns the file blob. */
  exportCustomers(params: CrmCustomersQuery, format: 'csv' | 'xlsx') {
    return this.http.get(`${this.CRM_URL}/customers/export`, {
      params: { ...params, format },
      responseType: 'blob',
    });
  }

  /** Mint a one-time 2-minute impersonation link (only when the profile has_user). */
  impersonateCustomer(id: string) {
    return this.http.post<CrmImpersonateResponse>(`${this.CRM_URL}/customers/${id}/impersonate`, {});
  }

  /**
   * Same one-time 2-minute link, keyed on the user instead of the CRM profile (PAUL-3287), for
   * surfaces that hold a user id and no customer id, such as the subscriptions grid.
   *
   * `redirectPath` is a portal ROUTE below the app's `/user` mount (`/user-details/...`), never a
   * URL and never carrying the mount itself: the backend grafts that on and rejects anything
   * outside the allowlist. Omit it to land on the portal's login-code page, as CRM does.
   */
  impersonateUser(userId: string, redirectPath?: string) {
    return this.http.post<CrmImpersonateResponse>(
      `${this.CRM_URL}/users/${userId}/impersonate`,
      {},
      { params: redirectPath ? { redirect_path: redirectPath } : {} }
    );
  }

  // --- Customer detail (§6) --------------------------------------------------
  getCustomer(id: string) {
    return this.http.get<CrmCustomerDetail>(`${this.CRM_URL}/customers/${id}`);
  }

  /** Full overwrite — always send `email` back (echo current when not changing it). */
  updateCustomer(id: string, data: CrmCustomerEdit) {
    return this.http.put<CrmCustomerDetail>(`${this.CRM_URL}/customers/${id}`, data);
  }

  /** Right to be forgotten — irreversible. */
  deleteCustomer(id: string) {
    return this.http.delete(`${this.CRM_URL}/customers/${id}`);
  }

  getCustomerOrders(id: string, params: { start?: number; length?: number }) {
    return this.http.get<DataTablesList<CrmCustomerOrderRow>>(`${this.CRM_URL}/customers/${id}/orders`, {
      params: { ...params },
    });
  }

  /** Bare array, max 200, newest first. */
  getCustomerReservations(id: string) {
    return this.http.get<CrmCustomerReservationRow[]>(`${this.CRM_URL}/customers/${id}/reservations`);
  }

  /** DataTables-paged, default page size 20. */
  getCustomerEmails(id: string, params: { start?: number; length?: number }) {
    return this.http.get<DataTablesList<CrmCustomerEmailRow>>(`${this.CRM_URL}/customers/${id}/emails`, {
      params: { ...params },
    });
  }

  // --- Import (§7) — multipart; same call for dry-run preview and apply -------
  importCustomers(data: FormData) {
    return this.http.post<CrmImportResult>(`${this.CRM_URL}/customers/import`, data);
  }

  // --- Audience lists (§8) ---------------------------------------------------
  getAudienceLists() {
    return this.http.get<CrmAudienceList[]>(`${this.CRM_URL}/audience-lists`);
  }

  getAudienceList(id: string) {
    return this.http.get<CrmAudienceListDetail>(`${this.CRM_URL}/audience-lists/${id}`);
  }

  /** null/empty definition = "everyone (subscribed, non-suppressed)". */
  createAudienceList(data: CrmAudienceListUpsert) {
    return this.http.post<CrmAudienceList>(`${this.CRM_URL}/audience-lists`, data);
  }

  /** `definition: null` = rename-only; a changed definition auto-rebuilds the snapshot. */
  updateAudienceList(id: string, data: CrmAudienceListUpsert) {
    return this.http.put<CrmAudienceList>(`${this.CRM_URL}/audience-lists/${id}`, data);
  }

  /** For an upload list this only recounts the members that still exist; it never widens the list. */
  refreshAudienceList(id: string) {
    return this.http.post<CrmAudienceList>(`${this.CRM_URL}/audience-lists/${id}/refresh`, {});
  }

  /**
   * Creates an upload list (PAUL-3691) from a csv/xlsx with an `email` column (part `File`) and/or
   * pasted `addresses`, with `name`, `shop_id` and `subscribe` (`'true'`/`'false'`) beside them.
   */
  uploadAudienceList(data: FormData) {
    return this.http.post<CrmAudienceListUploadResult>(`${this.CRM_URL}/audience-lists/upload`, data);
  }

  downloadAudienceList(id: string, format: 'csv' | 'xlsx') {
    return this.http.get(`${this.CRM_URL}/audience-lists/${id}/download`, {
      params: { format },
      responseType: 'blob',
    });
  }

  /**
   * Blocked with `crm_audience_list_in_use` while an unsent campaign still uses it — either as
   * that campaign's recipient set or from one of its targeted sections (PAUL-2997).
   */
  deleteAudienceList(id: string) {
    return this.http.delete(`${this.CRM_URL}/audience-lists/${id}`);
  }

  // --- Impersonation audit (§13) --------------------------------------------
  getImpersonationAudit(params: { start?: number; length?: number }) {
    return this.http.get<DataTablesList<CrmImpersonationAuditRow>>(`${this.CRM_URL}/impersonation-audit`, {
      params: { ...params },
    });
  }

  // --- Newsletters directory (§11) ------------------------------------------
  /** Default first; the default is auto-created on first read and is undeletable. */
  getNewsletters() {
    return this.http.get<CrmNewsletter[]>(`${this.CRM_URL}/newsletters`);
  }

  createNewsletter(data: CrmNewsletterUpsert) {
    return this.http.post<CrmNewsletter>(`${this.CRM_URL}/newsletters`, data);
  }

  updateNewsletter(id: string, data: CrmNewsletterUpsert) {
    return this.http.put<CrmNewsletter>(`${this.CRM_URL}/newsletters/${id}`, data);
  }

  /** Blocked with `newsletter_default_cannot_be_deleted` for the default. */
  deleteNewsletter(id: string) {
    return this.http.delete(`${this.CRM_URL}/newsletters/${id}`);
  }

  /** Replace-set of where this newsletter is offered at checkout (all-or-nothing). */
  setNewsletterPointsOfSale(id: string, data: CrmNewsletterPosOfferings) {
    return this.http.put<void>(`${this.CRM_URL}/newsletters/${id}/points-of-sale`, data);
  }

  /** Newsletters offered at a given POS — reverse view used to pre-fill the offerings dialog. */
  getPointOfSaleNewsletters(posId: string) {
    return this.http.get<CrmNewsletter[]>(`${this.CRM_URL}/points-of-sale/${posId}/newsletters`);
  }

  // --- AI audience helper (§12) — propose-only ------------------------------
  getAudienceHelperProposal(data: CrmAudienceHelperRequest) {
    return this.http.post<CrmAudienceHelperResponse>(`${this.CRM_URL}/audience-helper`, data);
  }
}
